// Shared shell for the public marketing pages — top-nav-only page navigation
// (§4.2 revised per operator: real routes, no side rail, no long single scroll).
import { Link, useLocation } from "react-router-dom";
import ClerkAuthControls from "@/components/ClerkAuthControls";

export const C = {
  bg: "#0a0a0a", card: "#111111", cardAlt: "#141414",
  accent: "#00c896", red: "#ff4d4d", amber: "#f59e0b", cyan: "#4fc3f7",
  text: "#e5e5e5", muted: "#888888", border: "#222222", borderHover: "#333333",
};

export const SectionLabel = ({ text, color }) => (
  <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color, fontFamily: "'JetBrains Mono', monospace" }}>{text}</p>
);

const NAV_LINKS = [
  { to: "/platform", label: "Inside the platform", testid: "nav-showcase" },
  { to: "/whats-new", label: "What's new", testid: "nav-whatsnew" },
  { to: "/capabilities", label: "Capabilities", testid: "nav-capabilities" },
  { to: "/pricing", label: "Pricing", testid: "nav-pricing" },
  { to: "/about", label: "About", testid: "nav-about" },
];

export const PublicNav = () => {
  const { pathname } = useLocation();
  return (
    <nav className="fixed top-0 w-full z-50" style={{ background: "rgba(10,10,10,0.88)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${C.border}` }}>
      <div className="max-w-6xl mx-auto px-5 h-12 flex items-center justify-between">
        <Link to="/" data-testid="nav-home" className="text-sm font-semibold tracking-tight hover:opacity-80" style={{ color: C.text }}>MarkovLab</Link>
        <div className="flex items-center gap-5 text-xs" style={{ color: C.muted }}>
          {NAV_LINKS.map(l => (
            <Link key={l.to} to={l.to} data-testid={l.testid}
              className={`${l.to === "/about" ? "" : "hidden sm:inline "}hover:opacity-80`}
              style={pathname === l.to ? { color: C.accent, fontWeight: 600 } : undefined}>
              {l.label}
            </Link>
          ))}
          <ClerkAuthControls compact />
        </div>
      </div>
    </nav>
  );
};

export const PublicFooter = () => (
  <footer className="py-10 px-5" style={{ borderTop: `1px solid ${C.border}` }}>
    <div className="max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs" style={{ color: C.muted }}>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-semibold" style={{ color: "#666" }}>MarkovLab</span>
          <Link to="/about" data-testid="footer-about" className="hover:opacity-80">About</Link>
          <Link to="/legal/terms" data-testid="footer-terms" className="hover:opacity-80">Terms</Link>
          <Link to="/legal/risk" data-testid="footer-risk" className="hover:opacity-80">Risk Disclosure</Link>
          <Link to="/legal/privacy" data-testid="footer-privacy" className="hover:opacity-80">Privacy</Link>
          <Link to="/legal/refund" data-testid="footer-refund" className="hover:opacity-80">Refund</Link>
          <Link to="/contact" data-testid="footer-contact" className="hover:opacity-80">Contact</Link>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1" data-testid="cloudflare-badge-landing">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            Protected by Cloudflare
          </span>
          <span>info@markovlab.app</span>
          <span>&copy; {new Date().getFullYear()} Concrete Control LLC (dba MarkovLab)</span>
        </div>
      </div>
      <p className="mt-4 text-[10px] leading-relaxed" style={{ color: "#444" }}>
        For educational and informational purposes only. Not investment advice. Options involve substantial
        risk and are not suitable for all investors. MarkovLab is not a broker-dealer or investment adviser.
        Users are responsible for their own investment decisions and any orders placed with their brokers.
      </p>
    </div>
  </footer>
);

// Page wrapper: nav + padded content + footer
export const PublicPage = ({ children }) => (
  <div style={{ background: C.bg, color: C.text, fontFamily: "'Inter', system-ui, sans-serif" }} className="min-h-screen flex flex-col">
    <PublicNav />
    <div className="flex-1 pt-12">{children}</div>
    <PublicFooter />
  </div>
);
